import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
import type { ChartDataPoint } from '@/services/dashboard/ChartQueryService';
import { formatNonUsCreDealDisplayName } from '@/utils/nonUsCreDashboardDisplay';

export type NonUsCreDashboardFilterOptions = {
  regions: string[];
  dealNames: string[];
  propertyTypes: string[];
};

const FILTER_OPTIONS_PATH = 'non-us-cre-dashboard/filter-options';
const AS_OF_DATES_PATH = 'non-us-cre-dashboard/as-of-dates';
const MATURITY_OUTLOOK_PATH = 'non-us-cre-dashboard/maturity-outlook';
const SECTOR_THEME_MIX_BY_FACILITY_PATH = 'non-us-cre-dashboard/sector-theme-mix-by-facility';
const COUNTRY_MIX_BY_FACILITY_PATH = 'non-us-cre-dashboard/country-mix-by-facility';

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

export type NonUsCreTapeDashboardKpiLatest = {
  asOfDateIso: string;
  outstandingBalance: number;
  weightedAvgLtvPct: number | null;
  weightedAvgIcr: number | null;
  weightedAvgSeasoningYears: number | null;
  /** ReadyCap <c>COUNT(*)</c> on the snapshot (loan-count footnote). */
  readyCapLoanCount: number;
  collateralItemCount: number;
  minInPlaceLtvPct: number | null;
  maxInPlaceLtvPct: number | null;
};

export type NonUsCreMaturityOutlookRow = {
  maturityMonthIso: string;
  loansMaturing: number;
  activeLoansRemaining: number;
};

export type NonUsCreSectorThemeMixByFacilityRow = {
  dealName: string;
  sectorTheme: string;
  collateralItemCount: number;
};

export type NonUsCreCountryMixByFacilityRow = {
  dealName: string;
  country: string;
  collateralItemCount: number;
};

function parseNum(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseKpiRow(raw: unknown): NonUsCreTapeDashboardKpiLatest | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDateIso ?? o.AsOfDateIso;
  if (typeof asOf !== 'string' || asOf.length === 0) {
    return null;
  }
  const outstandingBalance = parseNum(
    o.outstandingBalance ?? o.OutstandingBalance ?? o.totalWholeLoan ?? o.TotalWholeLoan,
  );
  const readyCapLoanCount = parseNum(
    o.readyCapLoanCount ?? o.ReadyCapLoanCount ?? o.collateralItemCount ?? o.CollateralItemCount,
  );
  const collateralItemCount = parseNum(
    o.collateralItemCount ?? o.CollateralItemCount ?? o.propertyCount ?? o.PropertyCount,
  );
  if (outstandingBalance == null || readyCapLoanCount == null) {
    return null;
  }

  return {
    asOfDateIso: asOf,
    outstandingBalance,
    weightedAvgLtvPct: parseNum(o.weightedAvgLtvPct ?? o.WeightedAvgLtvPct),
    weightedAvgIcr: parseNum(o.weightedAvgIcr ?? o.WeightedAvgIcr),
    weightedAvgSeasoningYears: parseNum(o.weightedAvgSeasoningYears ?? o.WeightedAvgSeasoningYears),
    readyCapLoanCount: Math.trunc(readyCapLoanCount),
    collateralItemCount: collateralItemCount != null ? Math.trunc(collateralItemCount) : Math.trunc(readyCapLoanCount),
    minInPlaceLtvPct: parseNum(o.minInPlaceLtvPct ?? o.MinInPlaceLtvPct),
    maxInPlaceLtvPct: parseNum(o.maxInPlaceLtvPct ?? o.MaxInPlaceLtvPct),
  };
}

function parseMaturityOutlookRows(raw: unknown): NonUsCreMaturityOutlookRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: NonUsCreMaturityOutlookRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const iso = r.maturityMonthIso ?? r.MaturityMonthIso;
    if (typeof iso !== 'string' || iso.length === 0) {
      continue;
    }
    const maturing = parseNum(r.loansMaturing ?? r.LoansMaturing);
    const remaining = parseNum(r.activeLoansRemaining ?? r.ActiveLoansRemaining);
    if (maturing == null || remaining == null) {
      continue;
    }
    out.push({
      maturityMonthIso: iso,
      loansMaturing: Math.trunc(maturing),
      activeLoansRemaining: Math.trunc(remaining),
    });
  }
  return out;
}

function parseSectorThemeMixByFacilityRows(raw: unknown): NonUsCreSectorThemeMixByFacilityRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: NonUsCreSectorThemeMixByFacilityRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const deal = r.dealName ?? r.DealName;
    const theme = r.sectorTheme ?? r.SectorTheme;
    if (typeof deal !== 'string' || deal.length === 0 || typeof theme !== 'string' || theme.length === 0) {
      continue;
    }
    const count = parseNum(r.collateralItemCount ?? r.CollateralItemCount);
    if (count == null) {
      continue;
    }
    out.push({
      dealName: deal,
      sectorTheme: theme,
      collateralItemCount: Math.trunc(count),
    });
  }
  return out;
}

function parseCountryMixByFacilityRows(raw: unknown): NonUsCreCountryMixByFacilityRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: NonUsCreCountryMixByFacilityRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const deal = r.dealName ?? r.DealName;
    const country = r.country ?? r.Country;
    if (typeof deal !== 'string' || deal.length === 0 || typeof country !== 'string' || country.length === 0) {
      continue;
    }
    const count = parseNum(r.collateralItemCount ?? r.CollateralItemCount);
    if (count == null) {
      continue;
    }
    out.push({
      dealName: deal,
      country,
      collateralItemCount: Math.trunc(count),
    });
  }
  return out;
}

export type NonUsCreDashboardFilters = {
  region: readonly string[];
  dealName: readonly string[];
  propertyType: readonly string[];
  /** Selected tape AS_OF_DATE (YYYY-MM-DD). Empty = latest. */
  asOfDate?: string;
};

function appendNonUsCreDashboardFilterParams(qs: URLSearchParams, filters: NonUsCreDashboardFilters) {
  for (const v of filters.region) {
    const t = v.trim();
    if (t) qs.append('region', t);
  }
  for (const v of filters.dealName) {
    const t = v.trim();
    if (t) qs.append('dealName', t);
  }
  for (const v of filters.propertyType) {
    const t = v.trim();
    if (t) qs.append('propertyType', t);
  }
  const asOf = filters.asOfDate?.trim();
  if (asOf) qs.set('asOfDate', asOf);
}

export class NonUsCreDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return {};
  };

  fetchNonUsCreDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      regions: stringArrayFromBody(body, 'regions', 'Regions'),
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
      propertyTypes: stringArrayFromBody(body, 'propertyTypes', 'PropertyTypes'),
    };
  };

  fetchNonUsCreDashboardAsOfDates = async () => {
    const body = await this.getJsonBody(AS_OF_DATES_PATH);
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchNonUsCreTapeDashboardKpi = async (filters: NonUsCreDashboardFilters) => {
    const qs = new URLSearchParams();
    appendNonUsCreDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `non-us-cre-dashboard/tape-kpi?${q}` : 'non-us-cre-dashboard/tape-kpi';

    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return null;
      }
      const parsed: unknown = JSON.parse(text) as unknown;
      return parseKpiRow(parsed);
    }
    return parseKpiRow(raw);
  };

  fetchNonUsCreMaturityOutlook = async (filters: NonUsCreDashboardFilters) => {
    const qs = new URLSearchParams();
    appendNonUsCreDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${MATURITY_OUTLOOK_PATH}?${q}` : MATURITY_OUTLOOK_PATH;
    const body = await this.getJsonBody(path);
    return parseMaturityOutlookRows(body);
  };

  fetchNonUsCreSectorThemeMixByFacility = async (filters: NonUsCreDashboardFilters) => {
    const qs = new URLSearchParams();
    appendNonUsCreDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${SECTOR_THEME_MIX_BY_FACILITY_PATH}?${q}` : SECTOR_THEME_MIX_BY_FACILITY_PATH;
    const body = await this.getJsonBody(path);
    return parseSectorThemeMixByFacilityRows(body);
  };

  fetchNonUsCreCountryMixByFacility = async (filters: NonUsCreDashboardFilters) => {
    const qs = new URLSearchParams();
    appendNonUsCreDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${COUNTRY_MIX_BY_FACILITY_PATH}?${q}` : COUNTRY_MIX_BY_FACILITY_PATH;
    const body = await this.getJsonBody(path);
    return parseCountryMixByFacilityRows(body);
  };
}

/** Snapshot mix rows → stacked-bar points (facility on X, dimension in series). */
export function nonUsCreSectorThemeMixRowsToChartDataPoints(
  rows: readonly NonUsCreSectorThemeMixByFacilityRow[],
): ChartDataPoint[] {
  return rows.map(row => ({
    xAxis: formatNonUsCreDealDisplayName(row.dealName),
    series: row.sectorTheme,
    value: row.collateralItemCount,
  }));
}

/** Snapshot mix rows → stacked-bar points (facility on X, country in series). */
export function nonUsCreCountryMixRowsToChartDataPoints(
  rows: readonly NonUsCreCountryMixByFacilityRow[],
): ChartDataPoint[] {
  return rows.map(row => ({
    xAxis: formatNonUsCreDealDisplayName(row.dealName),
    series: row.country,
    value: row.collateralItemCount,
  }));
}
